import { SchemaTypes } from '@allesauto/graphql';
import { openGraph } from '@allesauto/utils/funcs';
import Head from 'next/head';
import { useRouter } from 'next/router';
import * as React from 'react';

const allesAutoDefaults = {
  title: 'Alles.Auto',
  siteName: 'ALLES.AUTO',
  description:
    'Die Plattform für alles rund ums Auto – an einem Ort und mit großem Händlernetzwerk. Autos kaufen, verkaufen und Termine direkt online buchen.',
  url: 'https://alles.auto/',
  image: '/large-og-1200x630.png',
  type: 'website',
  robots: 'follow, index', // https://developers.google.com/search/docs/advanced/robots/robots_meta_tag
};

export type SeoProps = {
  date?: string;
  robots?: string;
  title?: string;
  description?: string;
  pageHeadline?: boolean;
  wl_title?: string;
  whiteLabel?: SchemaTypes.WhiteLabelResponse;
} & Partial<typeof allesAutoDefaults>;

export default function Seo({
  date,
  description,
  pageHeadline = true,
  robots,
  title,
  wl_title,
  whiteLabel,
}: SeoProps) {
  const router = useRouter();
  const [url, setUrl] = React.useState('');

  React.useEffect(() => {
    setUrl(window.location.href);
  }, []);

  // Start with defaults, extend with props
  const whiteLabelDefaults = whiteLabel && {
    title: whiteLabel.merchantName,
    siteName: whiteLabel.merchantName,
    url: `https://${whiteLabel.subdomain}`,
    description: '',
  };
  const meta = {
    ...allesAutoDefaults,
    ...whiteLabelDefaults,
    date,
  };

  // Use title if it was provided
  if (whiteLabel && wl_title) {
    meta.title = wl_title;
  } else {
    if (title) meta.title = title;
  }

  // Append sitename
  meta.title += ` | ${meta.siteName}`;

  // Append page description
  if (description) {
    meta.description = description;
  }
  //OG Image
  meta.image = openGraph({
    description: meta.description,
    siteName: title ? meta.siteName : meta.title, // Use siteName if there is title but show full title if there is none
    title: title,
  });

  // Use robots if it was provided
  if (robots) {
    meta.robots = robots;
  }

  // noindex, nofollow for whitelabel pages
  if (whiteLabel) {
    meta.robots = 'none';
    meta.description = '';
  }

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="robots" content={meta.robots} />
        <meta content={meta.description} name="description" />
        {router && (
          <>
            <meta property="og:url" content={url} />
            <link rel="canonical" href={url} />
          </>
        )}
        {/* Open Graph */}
        <meta property="og:type" content={meta.type} />
        <meta property="og:site_name" content={meta.siteName} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:title" content={meta.title} />
        <meta name="image" property="og:image" content={meta.image} />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        {/* <meta name="twitter:site" content="" /> */}
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />
        {meta.date && (
          <>
            <meta property="article:published_time" content={meta.date} />
            <meta
              name="publish_date"
              property="og:publish_date"
              content={meta.date}
            />
            <meta
              name="author"
              property="article:author"
              content={meta.siteName}
            />
          </>
        )}
      </Head>
      {pageHeadline && <h1 className="sr-only">{meta.title}</h1>}
    </>
  );
}
